package features.notifications.data

import core.firebase.FirebaseService
import features.notifications.domain.Notification
import kotlin.js.Promise
import kotlin.js.json

/**
 * Repository for managing user notifications in Firestore
 */
object NotificationRepository {
    
    /**
     * Create a notification for a user
     * @param userId User ID to receive the notification
     * @param type Notification type
     * @param title Notification title
     * @param message Notification message
     * @param relatedId Optional related entity ID
     * @return Promise
     */
    fun createNotification(
        userId: String,
        type: String,
        title: String,
        message: String,
        relatedId: String? = null
    ): Promise<Unit> {
        return Promise { resolve, reject ->
            try {
                val notificationData = json(
                    "userId" to userId,
                    "type" to type,
                    "title" to title,
                    "message" to message,
                    "isRead" to false,
                    "createdAt" to js("firebase.firestore.FieldValue.serverTimestamp()"),
                    "relatedId" to relatedId
                )
                
                FirebaseService.db
                    .collection("users")
                    .doc(userId)
                    .collection("notifications")
                    .add(notificationData)
                    .then {
                        console.log("✅ Notification created for user:", userId)
                        resolve(Unit)
                    }
                    .catch { error ->
                        console.error("❌ Create notification failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Create notification error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get all notifications for a user
     * @param userId User ID
     * @return Promise with list of notifications
     */
    fun getUserNotifications(userId: String): Promise<List<Notification>> {
        return Promise { resolve, reject ->
            try {
                FirebaseService.db
                    .collection("users")
                    .doc(userId)
                    .collection("notifications")
                    .get()
                    .then { snapshot: dynamic ->
                        val notifications = mutableListOf<Notification>()
                        snapshot.forEach { doc: dynamic ->
                            try {
                                val rawData: dynamic = doc.data()
                                
                                val notification = Notification(
                                    id = doc.id,
                                    userId = rawData.userId as? String ?: "",
                                    type = rawData.type as? String ?: "",
                                    title = rawData.title as? String ?: "",
                                    message = rawData.message as? String ?: "",
                                    isRead = rawData.isRead as? Boolean ?: false,
                                    createdAt = formatTimestamp(rawData.createdAt),
                                    relatedId = rawData.relatedId as? String
                                )
                                notifications.add(notification)
                            } catch (e: Throwable) {
                                console.error("❌ Error processing notification doc:", e)
                            }
                        }
                        console.log("✅ Loaded ${notifications.size} notifications")
                        resolve(notifications)
                    }
                    .catch { error: dynamic ->
                        console.error("❌ Get notifications failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Get notifications error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Mark a notification as read
     * @param userId User ID
     * @param notificationId Notification ID
     * @return Promise
     */
    fun markAsRead(userId: String, notificationId: String): Promise<Unit> {
        return Promise { resolve, reject ->
            try {
                val updateData = json("isRead" to true)
                
                FirebaseService.db
                    .collection("users")
                    .doc(userId)
                    .collection("notifications")
                    .doc(notificationId)
                    .update(updateData)
                    .then {
                        console.log("✅ Notification marked as read")
                        resolve(Unit)
                    }
                    .catch { error ->
                        console.error("❌ Mark as read failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Mark as read error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Mark all notifications as read for a user
     * @param userId User ID
     * @return Promise
     */
    fun markAllAsRead(userId: String): Promise<Unit> {
        return Promise { resolve, reject ->
            try {
                FirebaseService.db
                    .collection("users")
                    .doc(userId)
                    .collection("notifications")
                    .where("isRead", "==", false)
                    .get()
                    .then { snapshot: dynamic ->
                        val batch = FirebaseService.db.batch()
                        snapshot.forEach { doc: dynamic ->
                            batch.update(doc.ref, json("isRead" to true))
                        }
                        batch.commit()
                            .then {
                                console.log("✅ All notifications marked as read")
                                resolve(Unit)
                            }
                            .catch { error: dynamic ->
                                console.error("❌ Batch update failed:", error)
                                reject(error)
                            }
                    }
                    .catch { error: dynamic ->
                        console.error("❌ Get unread notifications failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Mark all as read error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get count of unread notifications
     * @param userId User ID
     * @return Promise with unread count
     */
    fun getUnreadCount(userId: String): Promise<Int> {
        return Promise { resolve, reject ->
            try {
                FirebaseService.db
                    .collection("users")
                    .doc(userId)
                    .collection("notifications")
                    .where("isRead", "==", false)
                    .get()
                    .then { snapshot: dynamic ->
                        val count = snapshot.size as Int
                        resolve(count)
                    }
                    .catch { error: dynamic ->
                        console.error("❌ Get unread count failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Get unread count error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Format Firestore timestamp to string
     */
    private fun formatTimestamp(timestamp: dynamic): String {
        return try {
            if (timestamp == null || timestamp == js("undefined")) {
                ""
            } else {
                val date: dynamic = timestamp.toDate()
                date.toISOString()
            }
        } catch (e: Throwable) {
            ""
        }
    }
}
